import i18next from 'i18next';
import ReactDomServer from 'react-dom/server';

import {
	IDiagram,
	IKeyRolesData,
	INotification,
	IParsedTableData,
	ISummaryStory,
	ITable,
	IUsecaseData,
	IUserStoryAcceptanceCriteria,
	IUserStoryScenario,
	TicketType,
} from 'store/ticket-service/types';

import { normalizeObjectKeys } from 'utils/normalizeObjectKeys';
import { orderDiagrams } from 'utils/orderDiagrams';

import { NamespacesList, TicketStages, UseCaseTableFields } from 'core/enums/common';

import { ITestCase, ITestPlan, IUserStoryTest } from 'components/TicketSteps/TestCasesStep/types';

const translate = (translaionPath: string) => {
	return i18next.t(translaionPath);
};

const pixelsInOneCentimeter = 38.8;

const maxImageWidthInCentimeters = 17;
const maxImageHeightInCentimeters = 23;

const storage = localStorage.getItem('i18nextLng');

const contentDirection = storage && storage === 'ar' ? 'rtl' : 'ltr';

const pixelsToCentimeters = (number: number) => {
	return number / pixelsInOneCentimeter;
};

const getLevelThreeSubTitleString = (text: string) => {
	const subTitleBlock = (
		<div dir={contentDirection}>
			<h3>{text}</h3>
		</div>
	);

	return btoa(unescape(encodeURIComponent(ReactDomServer.renderToStaticMarkup(subTitleBlock))));
};

const getSubTitleString = (text: string) => {
	const subTitleBlock = (
		<div dir={contentDirection}>
			<h2>{text}</h2>
		</div>
	);

	return btoa(unescape(encodeURIComponent(ReactDomServer.renderToStaticMarkup(subTitleBlock))));
};

const getTitleString = (title: string) => {
	const titleBlock = (
		<div dir={contentDirection}>
			<h1>{title}</h1>
		</div>
	);

	return btoa(unescape(encodeURIComponent(ReactDomServer.renderToStaticMarkup(titleBlock))));
};

const getIndexedTextString = (text: string, index: number, inBlock = false) => {
	const indexedTextBlock = inBlock ? (
		<div dir={contentDirection}>
			<p>{String(index + 1) + '.' + text}</p>
		</div>
	) : (
		<p dir={contentDirection}>{String(index + 1) + '.' + text}</p>
	);

	return btoa(unescape(encodeURIComponent(ReactDomServer.renderToStaticMarkup(indexedTextBlock))));
};

const getKeyValueString = (key: string, value: string) => {
	const keyValueTextBlock = (
		<>
			<p dir={contentDirection}>
				<strong>{key + ':'}</strong>
			</p>
			<p dir={contentDirection}>{value}</p>
		</>
	);

	return btoa(unescape(encodeURIComponent(ReactDomServer.renderToStaticMarkup(keyValueTextBlock))));
};

const getKeyValueListString = (key: string, value: Array<string>) => {
	const keyValueListString = [];

	const keyTextBlock = <strong>{key + ':'}</strong>;

	keyValueListString.push(
		btoa(unescape(encodeURIComponent(ReactDomServer.renderToStaticMarkup(keyTextBlock))))
	);

	value.forEach((item, index) => {
		keyValueListString.push(getIndexedTextString(item, index));
	});

	return keyValueListString;
};

const getDescriptionString = (description: string) => {
	const descriptionBlock = (
		<>
			<div dir={contentDirection}>
				<h2>{translate(`${NamespacesList['reportStep']}:titles.description`)}</h2>
			</div>
			<p dir={contentDirection}>{description}</p>
		</>
	);

	return btoa(unescape(encodeURIComponent(ReactDomServer.renderToStaticMarkup(descriptionBlock))));
};

const getUserScenarioString = (userScenario: IUserStoryScenario, index: number) => {
	const scenarioBlock = (
		<div dir={contentDirection}>
			<p>{String(index + 1) + '.' + userScenario.title}</p>
			<p>{userScenario.description}</p>
		</div>
	);

	return btoa(unescape(encodeURIComponent(ReactDomServer.renderToStaticMarkup(scenarioBlock))));
};

const getUserAcceptanceCriteriaString = (
	acceptanceCriteria: IUserStoryAcceptanceCriteria,
	index: number
) => {
	const scenarioBlock = (
		<div dir={contentDirection}>
			<p>{String(index + 1) + '.' + acceptanceCriteria.title}</p>
			<p>{acceptanceCriteria.description}</p>
		</div>
	);

	return btoa(unescape(encodeURIComponent(ReactDomServer.renderToStaticMarkup(scenarioBlock))));
};

const stringHasOnlyNumbers = (string: string) => {
	// If string contains only numbers, returns false
	const regex = new RegExp(/^\d+$/);

	return regex.test(string);
};

const cleanScenariosNumbering = (array: Array<string>) =>
	array.filter((value) => value && value.length > 1 && !stringHasOnlyNumbers(value));

const getUserStoriesString = (userStories: Array<ISummaryStory>) => {
	const userStoryString = [];

	userStoryString.push(
		getTitleString(translate(`${NamespacesList['reportStep']}:titles.userStories`))
	);

	userStories.forEach((story, index) => {
		// Ajouter une ligne de séparation avant chaque User Story (sauf la première)
		if (index > 0) {
			const separatorLine = (
				<div dir={contentDirection} style={{ textAlign: 'center', margin: '16px 0' }}>
					<hr style={{ width: '50%', margin: '0 auto', border: 'none', borderTop: '1px solid #6e6e6e' }} />
				</div>
			);
			userStoryString.push(
				btoa(unescape(encodeURIComponent(ReactDomServer.renderToStaticMarkup(separatorLine))))
			);
		}

		userStoryString.push(getIndexedTextString(story.data, index, true));

		if (story?.summaryScenarios?.length) {
			userStoryString.push(
				getLevelThreeSubTitleString(
					translate(`${NamespacesList['generalStep']}:commonText.scenarios`)
				)
			);

			story.summaryScenarios.forEach((scenario, index) => {
				userStoryString.push(getUserScenarioString(scenario, index));
			});
		}

		if (story?.summaryAcceptanceCriteria?.length) {
			userStoryString.push(
				getLevelThreeSubTitleString(
					translate(`${NamespacesList['generalStep']}:commonText.acceptanceCriteria`)
				)
			);

			story.summaryAcceptanceCriteria.forEach((acceptanceCriteria, index) => {
				userStoryString.push(getUserAcceptanceCriteriaString(acceptanceCriteria, index));
			});
		}
	});

	return userStoryString;
};

const getUseCaseTableRowString = ({ rowTitle, rowValue }: { rowTitle: string; rowValue: any }) => {
	let useCaseTableRowBlock;

	if (rowTitle !== UseCaseTableFields['Key Roles']) {
		useCaseTableRowBlock = (
			<div>
				<h4>{translate(`${NamespacesList['useCasesStep']}:descriptionTable.${rowTitle}`)}</h4>
				<br />
				{rowValue.map((arrayItem: string, index: number) => (
					<p key={index}>
						{index + 1}.{arrayItem}
					</p>
				))}
			</div>
		);
	} else {
		useCaseTableRowBlock = (
			<>
				<div dir={contentDirection}>
					<h4>{translate(`${NamespacesList['useCasesStep']}:descriptionTable.${rowTitle}`)}</h4>
				</div>
				{rowValue.map((arrayItem: IKeyRolesData) =>
					(Object.entries(arrayItem) as Array<[string, string]>).map(([key, value], index) => (
						<div key={index} dir={contentDirection}>
							<strong>
								{translate(`${NamespacesList['useCasesStep']}:descriptionTable.${key}`)}:{' '}
							</strong>
							<p>{value}</p>
						</div>
					))
				)}
			</>
		);
	}

	return btoa(
		unescape(encodeURIComponent(ReactDomServer.renderToStaticMarkup(useCaseTableRowBlock)))
	);
};

const getUseCaseTableString = (useCaseTable: ITable) => {
	const parsedTable: IParsedTableData = JSON.parse(useCaseTable.table);

	return Object.entries(parsedTable).map(([key, value]) =>
		getUseCaseTableRowString({
			rowTitle: key,
			rowValue: value,
		})
	);
};

const getDiagramImage = async (imageUrl: string): Promise<Record<string, any> | null> => {
	const response = await fetch(imageUrl);

	if (response.ok) {
		const imageBlob = await response.blob();
		const img = new Image();

		return new Promise((resolve) => {
			img.onload = () => {
				const width =
					pixelsToCentimeters(img.width) > maxImageWidthInCentimeters
						? maxImageWidthInCentimeters
						: pixelsToCentimeters(img.width);

				const height =
					pixelsToCentimeters(img.height) > maxImageHeightInCentimeters
						? maxImageHeightInCentimeters
						: pixelsToCentimeters(img.height);

				resolve({
					width,
					height,
				});
			};

			img.src = URL.createObjectURL(imageBlob);
		});
	} else {
		return new Promise((resolve) => resolve(null));
	}
};

const getDiagramString = async (diagram: IDiagram) => {
	if (diagram.pictureLink) {
		const imageData = await getDiagramImage(diagram.pictureLink.replace(/\/svg\//g, '/png/'));

		if (imageData !== null) {
			const { width, height } = imageData;
			// image styles do not use React syntax as they will be converted to string. So they can be detected.
			const diagramBlock = (
				<div dir={contentDirection}>
					<p>
						<strong>
							{translate(`${NamespacesList['useCasesStep']}:diagrams.${diagram.title}`)}
						</strong>
					</p>
					<div>
						<img
							alt={`${diagram.title} diagram`}
							src={diagram.pictureLink.replace(/\/svg\//g, '/png/')}
							style={{ width: `${width.toFixed(1)}cm`, height: `${height.toFixed(1)}cm` }}
						/>
					</div>
				</div>
			);

			return btoa(unescape(encodeURIComponent(ReactDomServer.renderToStaticMarkup(diagramBlock))));
		} else {
			return null;
		}
	}

	return null;
};

const getUseCaseDiagramsString = async (diagrams: Array<IDiagram>) =>
	await Promise.all(
		orderDiagrams(diagrams).map(async (diagram: IDiagram) => {
			return await getDiagramString(diagram);
		})
	);

const getUseCasesString = async (useCases: Array<IUsecaseData>) => {
	const useCasesString = [];

	if (useCases?.length) {
		useCasesString.push(
			getSubTitleString(translate(`${NamespacesList['reportStep']}:titles.useCases`))
		);

		for await (const [index, useCase] of useCases.entries() as IterableIterator<
			[number, IUsecaseData]
		>) {
			useCasesString.push(getLevelThreeSubTitleString(`${index + 1}.${useCase.title || ''}`));

			getUseCaseTableString(useCase.tables[0]).forEach((encodedString) =>
				useCasesString.push(encodedString)
			);

			const diagramStringsArray = await getUseCaseDiagramsString(useCase.diagrams);

			diagramStringsArray.forEach((encodedString) => {
				if (encodedString) useCasesString.push(encodedString);
			});
		}
	}

	return useCasesString;
};

const getNotificationString = (notification: INotification) => {
	const notificationBlock = (
		<div dir={contentDirection}>
			<strong>{notification.title}</strong>
			<p>{notification.description}</p>
			<p>
				{translate(`${NamespacesList['notificationsStep']}:notifications.Email`)} -{' '}
				<strong>{notification.email ? 'true' : 'false'}</strong>
			</p>
			<p>
				{translate(`${NamespacesList['notificationsStep']}:notifications.SMS`)} -{' '}
				<strong>{notification.sms ? 'true' : 'false'}</strong>
			</p>
			<p>
				{translate(`${NamespacesList['notificationsStep']}:notifications.Push`)} -{' '}
				<strong>{notification.push ? 'true' : 'false'}</strong>
			</p>
		</div>
	);

	return btoa(unescape(encodeURIComponent(ReactDomServer.renderToStaticMarkup(notificationBlock))));
};

const getTestPlanBlockString = (testPlan: ITestPlan) => {
	const testPlanString = [];

	Object.entries(testPlan).forEach(([key, value]) => {
		if (key !== 'id' && value?.length) {
			testPlanString.push(
				getKeyValueString(translate(`${NamespacesList['testCasesStep']}:testPlan.${key}`), value)
			);
		}
	});

	testPlanString.push(
		btoa(unescape(encodeURIComponent(ReactDomServer.renderToStaticMarkup(<br />))))
	);

	return testPlanString;
};

const getTestScenarioBlockString = (testScenarios: Array<string>) => {
	const testScenariosString = [];

	testScenariosString.push(
		getLevelThreeSubTitleString(translate(`${NamespacesList['testCasesStep']}:testScenarios.title`))
	);

	testScenarios.forEach((scenario, index) => {
		testScenariosString.push(getIndexedTextString(scenario, index));
	});

	testScenariosString.push(
		btoa(unescape(encodeURIComponent(ReactDomServer.renderToStaticMarkup(<br />))))
	);

	return testScenariosString;
};

const getTestCasesBlockString = (testCases: Array<ITestCase>) => {
	const testCasesString = [];

	testCasesString.push(
		getLevelThreeSubTitleString(translate(`${NamespacesList['testCasesStep']}:testCases.title`))
	);

	testCases.forEach((testCase: ITestCase) => {
		Object.entries(testCase).forEach(([key, value]) => {
			if (key !== 'id') {
				if (Array.isArray(value)) {
					getKeyValueListString(
						translate(`${NamespacesList['testCasesStep']}:testCases.${key}`),
						value
					).forEach((encodedString) => testCasesString.push(encodedString));
				} else {
					testCasesString.push(
						getKeyValueString(
							translate(`${NamespacesList['testCasesStep']}:testCases.${key}`),
							value
						)
					);
				}
			}
		});

		testCasesString.push(
			btoa(unescape(encodeURIComponent(ReactDomServer.renderToStaticMarkup(<br />))))
		);
	});

	return testCasesString;
};

const getTestsBlockString = (tests: Array<IUserStoryTest>) => {
	const testsString = [];

	if (tests?.length) {
		testsString.push(
			getSubTitleString(translate(`${NamespacesList['reportStep']}:titles.testCases`))
		);

		const cleanedTests = tests
			? tests.map((test) => ({
					...test,
					testScenarios: cleanScenariosNumbering(test.testScenarios),
					useCaseTitle: normalizeObjectKeys(test)['usecasetitle'],
			  }))
			: [];

		cleanedTests.forEach((userStoryTest, index) => {
			testsString.push(
				getLevelThreeSubTitleString(
					`${translate(`${NamespacesList['testCasesStep']}:testPlan.title`)}: ${index + 1}.${normalizeObjectKeys(userStoryTest)['usecasetitle'] || ''}`
				)
			);

			getTestPlanBlockString(userStoryTest.testPlan).forEach((encodedString) =>
				testsString.push(encodedString)
			);

			getTestScenarioBlockString(userStoryTest.testScenarios).forEach((encodedString) =>
				testsString.push(encodedString)
			);

			getTestCasesBlockString(userStoryTest.testCases).forEach((encodedString) =>
				testsString.push(encodedString)
			);
		});
	}

	return testsString;
};

const getNotificationsBlockString = (notifications: Array<INotification>) => {
	const notificationsString = [];

	notificationsString.push(
		getSubTitleString(translate(`${NamespacesList['reportStep']}:titles.notifications`))
	);

	notifications.forEach((notification: INotification) => {
		notificationsString.push(getNotificationString(notification));
	});

	return notificationsString;
};

const getBusinessImpactString = (businessImpact: Array<string>) => {
	const businessImpactString = [];

	businessImpactString.push(
		getSubTitleString(translate(`${NamespacesList['reportStep']}:titles.businessImpact`))
	);

	businessImpact.forEach((impactItem, index) => {
		businessImpactString.push(getIndexedTextString(impactItem, index));
	});

	return businessImpactString;
};

export const TicketToBase64 = async (ticketData: TicketType, activeStep: TicketStages) => {
	const reportSummarySting: string[] = [];

	const businessImpact = ticketData?.businessImpact?.length
		? JSON.parse(ticketData.businessImpact)
		: null;

	reportSummarySting.push(getTitleString(ticketData.title));

	if (activeStep === TicketStages.general || activeStep === TicketStages.report) {
		reportSummarySting.push(getDescriptionString(ticketData.description));

		getUserStoriesString(ticketData?.ticketSummaries || []).forEach((encodedString) =>
			reportSummarySting.push(encodedString)
		);
	}

	if (activeStep === TicketStages.useCases || activeStep === TicketStages.report) {
		const useCasesStringsArray = await getUseCasesString(ticketData.useCases);

		useCasesStringsArray.forEach((encodedString: string) => reportSummarySting.push(encodedString));
	}

	if (activeStep === TicketStages.testCases || activeStep === TicketStages.report) {
		getTestsBlockString(ticketData.userStoryTests).forEach((encodedString) => {
			reportSummarySting.push(encodedString);
		});
	}

	if (activeStep === TicketStages.notifications || activeStep === TicketStages.report) {
		getNotificationsBlockString(ticketData.notifications).forEach((encodedString) =>
			reportSummarySting.push(encodedString)
		);
	}

	if (businessImpact !== null && activeStep === TicketStages.report) {
		getBusinessImpactString(businessImpact).forEach((encodedString) => {
			reportSummarySting.push(encodedString);
		});
	}

	return reportSummarySting;
};
